/* ═══════════════════════════════════════════════════════════════════════════
   THE ROUTER

   Five surfaces in one Vite app:

     avtale.net/                the customer — the marketplace and the mirror
     avtale.net/{slug}          the business's own public website
     avtale.net/business        the tenant's dashboard
     avtale.net/staff           one member of staff's day
     avtale.net/admin           the platform

   The interesting line is the second one. Any segment that is not a reserved
   word is a business, which means the reserved list is not a convenience — it
   is the only thing keeping `avtale.net/admin` from being a business called
   "admin". It lives in api/local.js next to the function that hands slugs out,
   and both sides import it from there so it cannot drift.

   No router library. Not out of principle — the whole map is five names and a
   slug, and a dependency that resolves five names is a dependency to keep
   upgraded forever. If nested routes ever arrive, this is the file to throw
   away.

   Three ways to name a surface, on purpose:

     /business          the real one. What ships, and what Google sees
     ?surface=business  survives a static file server that has no rewrite rule
     #/business         survives file://, which is how a preview gets opened

   The last two exist because the app has to be openable before the server in
   front of it is configured — and a preview nobody can open is not a preview.
   ═══════════════════════════════════════════════════════════════════════ */

import { RESERVED } from './api/local.js';

/** The surfaces a path can name. Everything else is a business slug. */
export const SURFACES = ['customer', 'business', 'staff', 'admin'];

/** Is the hash the address, rather than an in-page anchor? */
export const hashMode = () => import.meta.env?.VITE_ROUTER === 'hash';

/* The last address this router resolved.
 *
 * It exists because of one collision that only happens in hash mode, and it
 * broke every tenant site in the preview and in the published build:
 *
 *   the address lives in the hash          →  #/hovden/
 *   a section link is also a hash          →  #enquiry
 *   pressing "Request a meeting" replaces  →  the address is gone
 *
 * The router then had nothing to read, fell back to the marketplace, and the
 * lawyer's own site vanished under a list of businesses. In path mode the two
 * never touch, so nothing here ever showed it — including the browser test
 * that walks every section link on every trade, because it runs against the
 * path build.
 *
 * `sectionHref` below stops the hash being written in the first place. This
 * is the second line of defence, for a bare hash that arrives some other way:
 * a pasted link, a prerendered anchor, the back button. Where we were is a
 * better answer than the front page. */
let lastAddress = null;

const ALIAS = { '': 'customer', 'index.html': 'customer', 'platform': 'admin' };

/**
 * Where are we?
 * @returns {{surface: string, slug: string|null}}
 */
export function route(loc = typeof window === 'undefined' ? null : window.location) {
  if (!loc) return { surface: 'customer', slug: null };

  const query = new URLSearchParams(loc.search || '');
  const forced = query.get('surface');
  if (forced) {
    if (forced === 'website') return { surface: 'website', slug: query.get('slug') };
    if (SURFACES.includes(forced)) return { surface: forced, slug: null };
  }

  /* An ADDRESS hash starts with "#/" — that is what `navigate()` writes, and
     it is the only form this router accepts.

     A bare "#booking" is an in-page anchor, not an address, and reading it as
     one was a bug that broke every business page in the app: the "Book
     appointment" button is `href="#booking"`, the router saw the segment
     "booking", decided it was a business slug, and drew "No page here —
     nothing is published at avtale.net/booking". The same for #services,
     #staff, #contact, #products, #enquiry, #reviews and #menu. Every section
     link on every tenant's own site, on all fourteen trades.

     The rule is now one character long and it is the right one: with the
     slash it is ours, without it, it belongs to the page. */
  const raw = String(loc.hash || '');
  if (raw.startsWith('#/')) {
    const hash = raw.slice(2);
    if (hash) return remember(fromParts(hash.split('/').filter(Boolean)));
  }

  /* A preview host serves the app from a path we do not own — the last segment
     of it is somebody's id, not a business slug, and reading it as one lands
     every visit on "no such business". Built with VITE_ROUTER=hash, the path is
     ignored entirely and the hash is the only address. The real site never sets
     it, because there the path is exactly what we want to read. */
  if (hashMode()) {
    /* A bare "#enquiry" is an in-page anchor and says nothing about where we
       are, so it must not move us. Staying put is the answer; the front page
       is the bug this line exists to stop. */
    if (raw && !raw.startsWith('#/') && lastAddress) return lastAddress;
    return remember({ surface: 'customer', slug: null });
  }

  /* The path. `index.html` is dropped first, so /lykke/index.html — which is
     what the prerender writes and what a plain static server serves — is the
     same route as /lykke. */
  const parts = String(loc.pathname || '/').split('/').filter(Boolean);
  if (parts[parts.length - 1] === 'index.html') parts.pop();
  return fromParts(parts);
}

/* ── The code on the table ────────────────────────────────────────────────

   `avtale.net/{slug}/bord/{n}` is what the card on table 7 points at, and it
   is the only address on this platform with two meaningful segments. The
   router reads the LAST segment everywhere else, which would have made
   table 7 a business called "7" — so the table is looked for first and the
   ordinary rule applies to everything that is not one.

   `bord` rather than `table`: it is printed on a card in a Norwegian
   restaurant and read by people standing next to it. */
function fromParts(parts) {
  const last = parts[parts.length - 1] || '';
  const before = parts[parts.length - 2] || '';
  const slug = parts[parts.length - 3];
  if (slug && /^\d{1,3}$/.test(last) && before.toLowerCase() === 'bord') {
    /* Resolve the SLUG segment and answer with that whatever it turns out to
       be. Falling back to the last segment instead made `/admin/bord/7` a
       business called "7" — the number is only ever a table, never an
       address of its own. */
    const at = fromSegment(slug);
    return at.surface === 'website' ? { ...at, table: Number(last) } : at;
  }
  return fromSegment(last);
}

const remember = at => { lastAddress = at; return at; };

/** For tests, which must not inherit the address the last one resolved. */
export const forgetAddress = () => { lastAddress = null; };

/**
 * The href for a link to a section of the page the visitor is already on.
 *
 * In path mode this is an ordinary anchor and the browser does the work —
 * which also means it works with no JavaScript at all, which is how a crawler
 * and the prerendered page see it.
 *
 * In hash mode the hash is the address, so writing an anchor into it would
 * throw the address away. The href stays for the same no-JavaScript reason;
 * `onSection` below stops the browser acting on it and scrolls instead.
 */
export const sectionHref = name => '#' + name;

/**
 * The click handler that goes with it. Pass the section name; in hash mode it
 * scrolls and leaves the address alone, and in path mode it does nothing and
 * lets the anchor behave like an anchor.
 */
export function onSection(name, e) {
  if (!hashMode() || typeof document === 'undefined') return;
  const el = document.getElementById(name);
  if (!el) return;
  e.preventDefault();
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function fromSegment(seg) {
  const s = decodeURIComponent(String(seg || '')).toLowerCase();
  const named = ALIAS[s] || (SURFACES.includes(s) ? s : null);
  if (named) return { surface: named, slug: null };
  if (RESERVED.includes(s)) return { surface: 'customer', slug: null };
  return { surface: 'website', slug: s };
}

/** Go somewhere without reloading. `to` is a path: '/', '/admin', '/lykke'. */
export function navigate(to) {
  if (typeof window === 'undefined') return;
  /* In hash mode the path is not ours to write — see route() above. */
  const url = import.meta.env?.VITE_ROUTER === 'hash'
    ? '#' + (to === '/' ? '/' : to)
    : to;
  window.history.pushState({}, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** Where the card on a table points. The code IS the table — see
    domain/tablesession.js — so this is the whole of what a guest scans. */
export const tablePath = (slug, table) => `/${slug}/bord/${table}`;

/** The path for a surface, so no component has to build one by hand. */
export const pathFor = (surface, slug) =>
  surface === 'website' ? '/' + slug : surface === 'customer' ? '/' : '/' + surface;
